/**
 * Decodificação da linha de comando, como `TCommandManager.TryDecodeCommandRequest`.
 * Regras em docs/spec/chat-commands.md.
 */

/** Pedido de comando: identificador e argumentos crus, ambos aparados. */
export type CommandRequest = {
  /** identificador como digitado, sem o prefixo */
  readonly commandId: string;
  /** tudo depois do primeiro separador, aparado */
  readonly args: string;
};

/**
 * Compara gatilhos como o `TUnicodeStringEqualityComparer` do original: sem caixa e sem
 * diacríticos.
 */
export function foldTrigger(text: string): string {
  return text
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** Glifos que encerram o identificador do comando (espaços e separadores Unicode). */
export function isCommandSeparator(glyph: string): boolean {
  return /^\s$/u.test(glyph);
}

/**
 * Lê a linha em um pedido. `skip` é quanto pular no começo — 1 para o prefixo `/`, 0 para o
 * console. Devolve `null` quando não sobra identificador.
 *
 * A varredura procura o separador a partir do início da linha (e não depois do prefixo),
 * como no original; um separador no primeiro caractere conta como "nenhum separador".
 */
export function decodeCommandRequest(line: string, skip = 0): CommandRequest | null {
  let separatorAt = -1;
  let offset = 0;
  for (const glyph of line) {
    if (isCommandSeparator(glyph)) {
      separatorAt = offset;
      break;
    }
    offset += glyph.length;
  }

  let commandId: string;
  let args: string;
  if (separatorAt < 1) {
    commandId = line.slice(skip).trim();
    args = '';
  } else {
    commandId = line.slice(skip, separatorAt).trim();
    args = line.slice(separatorAt + 1).trim();
  }

  if (commandId.length === 0) return null;
  return { commandId, args };
}

/**
 * Apara, descarta vazios e descarta repetidos sem caixa/diacríticos, preservando a ordem —
 * `TCommandManager.NormalizeTriggers`. O primeiro gatilho é o nome canônico.
 */
export function normalizeTriggers(triggers: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of triggers) {
    const trigger = raw.trim();
    if (trigger.length === 0) continue;
    const key = foldTrigger(trigger);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trigger);
  }
  return out;
}
